import { withRequestDeadline } from '../sync/requestDeadline'
import {
  FONT_CATALOG_INDEX_URL,
  FONT_CATALOG_MANIFEST_URL,
  getFontCatalogFamilyUrl,
  getFontCatalogFileUrl,
} from './fontCatalogConfig'
import {
  readCachedIndex,
  readCoreFontBytes,
  readInstalledFamily,
  writeCachedIndex,
  writeCoreFontBytes,
} from './fontCatalogStorage'
import type {
  FontCatalogFamily,
  FontCatalogFile,
  FontCatalogIndex,
  FontCatalogStyle,
  FontCatalogSummary,
} from '../../types/fontCatalog'

const lazyFaces = new Map<string, FontFace>()
const cachedLazyFaces = new Set<string>()
const registeredFaces = new Set<string>()
const knownFamiliesByName = new Map<string, FontCatalogFamily>()
const detailsById = new Map<string, FontCatalogFamily>()

function familyNameKey(name: string): string {
  return name.trim().toLowerCase()
}

function rememberCatalogFamily(family: FontCatalogFamily): void {
  knownFamiliesByName.set(familyNameKey(family.family), family)
}

function faceKey(file: FontCatalogFile): string {
  return `${file.path}\u0000${file.weight}\u0000${file.style}\u0000${file.unicodeRange}`
}

function isCatalogFile(value: unknown): value is FontCatalogFile {
  if (!value || typeof value !== 'object') return false
  const file = value as Record<string, unknown>
  return typeof file.path === 'string'
    && typeof file.weight === 'string'
    && (file.style === 'normal' || file.style === 'italic')
    && typeof file.unicodeRange === 'string'
}

function isCatalogSummary(value: unknown): value is FontCatalogSummary {
  if (!value || typeof value !== 'object') return false
  const family = value as Record<string, unknown>
  return typeof family.id === 'string'
    && typeof family.family === 'string'
    && typeof family.license === 'string'
    && typeof family.designer === 'string'
    && typeof family.category === 'string'
    && Array.isArray(family.subsets)
    && family.subsets.every((subset) => typeof subset === 'string')
    && (family.files === undefined || (Array.isArray(family.files) && family.files.every(isCatalogFile)))
}

function hasFiles(family: FontCatalogSummary): family is FontCatalogFamily {
  return Array.isArray(family.files)
}

/** Accepts index.json, or a legacy manifest.json whose entries also carry files. */
function parseFontCatalogIndex(value: unknown): FontCatalogIndex {
  if (!value || typeof value !== 'object' || !Array.isArray((value as { fonts?: unknown }).fonts)) {
    throw new Error('Font catalog index does not contain a fonts array')
  }
  const fonts = (value as { fonts: unknown[] }).fonts
  for (const family of fonts) {
    if (!isCatalogSummary(family)) {
      const id = family && typeof family === 'object' ? (family as { id?: unknown }).id : null
      throw new Error(`Font catalog contains an invalid family entry: ${String(id || 'unknown')}`)
    }
  }
  const index = { fonts: fonts as FontCatalogSummary[] }
  for (const family of index.fonts) if (hasFiles(family)) rememberCatalogFamily(family)
  return index
}

/** Returns a full family already loaded by an install, download or legacy manifest. */
export function findKnownCatalogFamilyByName(name: string): FontCatalogFamily | null {
  return knownFamiliesByName.get(familyNameKey(name)) ?? null
}

/**
 * Revalidates through the browser HTTP cache. Cloudflare Pages supplies an
 * ETag, so `cache: no-cache` produces a conditional request without manually
 * setting If-None-Match (which would trigger a rejected CORS preflight).
 * A catalog that predates index.json (404) is read from manifest.json.
 */
async function refreshFontCatalog(): Promise<FontCatalogIndex> {
  let response = await fetch(FONT_CATALOG_INDEX_URL, { cache: 'no-cache' })
  if (response.status === 404) response = await fetch(FONT_CATALOG_MANIFEST_URL, { cache: 'no-cache' })
  if (!response.ok) {
    throw new Error(`Font catalog request failed (${response.status})`)
  }
  const index = parseFontCatalogIndex(await response.json())
  await writeCachedIndex(index, response.headers.get('etag'))
  return index
}

export async function getFontCatalog(): Promise<{
  index: FontCatalogIndex
  refresh: Promise<FontCatalogIndex> | null
}> {
  const cached = await readCachedIndex()
  if (cached) {
    try {
      return { index: parseFontCatalogIndex(cached), refresh: refreshFontCatalog() }
    } catch {
      // Ignore an obsolete/corrupt local cache and replace it from upstream.
    }
  }
  return { index: await refreshFontCatalog(), refresh: null }
}

/** The catalog entry for a family name, from the cached index (no network). */
export async function findCachedCatalogSummary(name: string): Promise<FontCatalogSummary | null> {
  const key = familyNameKey(name)
  return (await readCachedIndex())?.fonts.find((family) => familyNameKey(family.family) === key) ?? null
}

/**
 * The full family (file list included) for a catalog entry. Uses, in order:
 * the entry itself when it came from a legacy manifest, this session's
 * copy, the installed copy if its fingerprint still matches, and finally
 * families/<id>.json. The fingerprint is kept on the result so a stored
 * install can later be recognised as current.
 */
export async function getCatalogFamilyDetail(
  summary: FontCatalogSummary,
  signal?: AbortSignal,
): Promise<FontCatalogFamily> {
  if (hasFiles(summary)) return summary
  const known = detailsById.get(summary.id)
  if (known && known.detailHash === summary.detailHash) return known

  const installed = await readInstalledFamily(summary.id)
  let family: FontCatalogFamily
  if (installed && summary.detailHash && installed.detailHash === summary.detailHash) {
    family = installed
  } else {
    const detail = await withRequestDeadline(async bounded => {
      const response = await fetch(getFontCatalogFamilyUrl(summary.id, summary.detailHash), { signal: bounded })
      if (!response.ok) throw new Error(`Could not look up ${summary.family} in the font catalog (${response.status})`)
      return response.json() as Promise<unknown>
    }, { signal })
    if (!isCatalogSummary(detail) || !hasFiles(detail) || detail.id !== summary.id) {
      throw new Error(`The font catalog's entry for ${summary.family} is damaged`)
    }
    family = { ...detail, detailHash: summary.detailHash }
  }
  detailsById.set(family.id, family)
  rememberCatalogFamily(family)
  return family
}

function codepointRanges(file: FontCatalogFile): Array<[number, number]> {
  return [...file.unicodeRange.toUpperCase().matchAll(/U\+([0-9A-F]+)(?:-([0-9A-F]+))?/g)]
    .map((match) => [Number.parseInt(match[1], 16), Number.parseInt(match[2] || match[1], 16)])
}

/** True when the file has every letter A–Z and a–z. */
export function coversBasicLatin(file: FontCatalogFile): boolean {
  const ranges = codepointRanges(file)
  const has = (codepoint: number) => ranges.some(([start, end]) => start <= codepoint && codepoint <= end)
  for (let codepoint = 0x41; codepoint <= 0x7a; codepoint++) {
    if ((codepoint <= 0x5a || codepoint >= 0x61) && !has(codepoint)) return false
  }
  return true
}

function codepointCount(file: FontCatalogFile): number {
  return codepointRanges(file).reduce((sum, [start, end]) => sum + end - start + 1, 0)
}

function preferredWeight(files: FontCatalogFile[]): FontCatalogFile[] {
  const variable = files.filter((file) => file.weight.trim().split(/\s+/).length === 2)
  if (variable.length) return variable
  const regular = files.filter((file) => file.weight.trim() === '400')
  return regular.length ? regular : files
}

function chooseCoreFile(files: FontCatalogFile[]): FontCatalogFile | null {
  const latin = files.filter((file) => /-latin\.woff2$/i.test(file.path))
  const candidates = latin.length > 0 ? latin : files.filter(coversBasicLatin)
  if (candidates.length > 0) return preferredWeight(candidates)[0]
  // A font for another script, or of symbols only: its core face is the file
  // covering the most characters, which for a script font is that script.
  const fallback = preferredWeight(files)
  return fallback.reduce<FontCatalogFile | null>(
    (best, file) => (!best || codepointCount(file) > codepointCount(best) ? file : best),
    null,
  )
}

/** Selects the one eagerly loaded core face for each style in a family. */
export function getCoreFontFiles(family: FontCatalogFamily): FontCatalogFile[] {
  const styles = new Set<FontCatalogStyle>(family.files.map((file) => file.style))
  return [...styles].flatMap((style) => {
    const core = chooseCoreFile(family.files.filter((file) => file.style === style))
    return core ? [core] : []
  })
}

function fontFaceDescriptors(file: FontCatalogFile): FontFaceDescriptors {
  return {
    weight: file.weight,
    style: file.style,
    unicodeRange: file.unicodeRange,
  }
}

async function addLazyFace(family: FontCatalogFamily, file: FontCatalogFile): Promise<void> {
  const key = faceKey(file)
  const bytes = await readCoreFontBytes(file.path)
  if (registeredFaces.has(key) && (!bytes || cachedLazyFaces.has(key))) return
  const previous = lazyFaces.get(key)
  if (previous) document.fonts.delete(previous)
  const source = bytes || `url("${getFontCatalogFileUrl(file.path)}")`
  const face = new FontFace(family.family, source, fontFaceDescriptors(file))
  if (bytes) await face.load()
  document.fonts.add(face)
  lazyFaces.set(key, face)
  if (bytes) cachedLazyFaces.add(key)
  registeredFaces.add(key)
}

async function loadCoreFace(
  family: FontCatalogFamily,
  file: FontCatalogFile,
  fetchMissing: boolean,
): Promise<void> {
  const key = faceKey(file)
  if (registeredFaces.has(key)) return

  const bytes = await readCatalogFontBytes(family, file, fetchMissing)
  if (!bytes) {
    throw new Error(`The local core file for ${family.family} is missing`)
  }

  const face = new FontFace(family.family, bytes, fontFaceDescriptors(file))
  await face.load()
  document.fonts.add(face)
  registeredFaces.add(key)
}

/** Reads a catalog file from the existing font cache, fetching it only when allowed. */
export async function readCatalogFontBytes(
  family: FontCatalogFamily,
  file: FontCatalogFile,
  fetchMissing: boolean,
  signal?: AbortSignal,
): Promise<ArrayBuffer | null> {
  let bytes = await readCoreFontBytes(file.path)
  if (!bytes && fetchMissing) {
    bytes = await withRequestDeadline(async bounded => {
      const response = await fetch(getFontCatalogFileUrl(file.path), { signal: bounded })
      if (!response.ok) throw new Error(`Failed to download ${family.family} (${response.status})`)
      return response.arrayBuffer()
    }, { signal })
    if (!bytes.byteLength) throw new Error('Empty font file')
  }
  // Also promote readable native legacy files during an explicit download or
  // installation. Unchanged primary cache entries avoid a redundant write.
  if (bytes && fetchMissing) await writeCoreFontBytes(file.path, bytes)
  return bytes
}

export async function registerCatalogFamily(
  family: FontCatalogFamily,
  options: { fetchMissingCore: boolean },
): Promise<void> {
  rememberCatalogFamily(family)
  if (typeof document === 'undefined' || typeof FontFace === 'undefined') {
    throw new Error('This browser does not support the CSS Font Loading API')
  }

  const coreFiles = getCoreFontFiles(family)
  if (coreFiles.length === 0) {
    throw new Error(`The font catalog lists no files for ${family.family}`)
  }
  await Promise.all(coreFiles.map((file) => loadCoreFace(family, file, options.fetchMissingCore)))
  for (const file of family.files) {
    if (!coreFiles.includes(file)) await addLazyFace(family, file)
  }
}
